import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {PrettyPrinter} from "../../../components/Utils/PrettyPrinter";
import {MarketingTabMapper} from "../utils/MarketingTabMapper";
import "./Chart.scss";

interface BarChartGroupElement {
    name: string;
    color: string;
    value: number;
}

export interface BarChartGroup {
    name: string;
    elements: BarChartGroupElement[];
}

interface BarChartProps {
    width: number;
    height: number;
    color: string;
    data: BarChartGroup[];
    yUnitOfMeasure: string;
}

interface BarChartState {
    selectedItem: string;
}

export class BarChart extends React.Component<BarChartProps, BarChartState> {

    private static DEFAULT_COLOR = "var(--vebox-gray-hover-light)";
    /** The color of histogram bars. */
    private static BAR_COLOR: string = "#75ca7e";

    private randomElementsPrefix: string;

    constructor(props: BarChartProps) {
        super(props);
        this.state = {selectedItem: null};
        this.setSelectedItem = this.setSelectedItem.bind(this);
        this.randomElementsPrefix = Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, "")
            .substr(0, 10);
    }

    public setSelectedItem(itemName: string): void {
        this.setState({selectedItem: itemName});
    }

    public render() {
        if (this.props.width <= 0 || this.props.height <= 0) {
            return null;
        }
        // Create the element
        const div = new ReactFauxDOM.Element("div");
        const data = this.props.data;
        let color = BarChart.BAR_COLOR;
        if (this.props.color && this.props.color != "") {
            color = this.props.color;
        }
        const margin = {top: 0, right: 20, bottom: 30, left: 40},
            width = this.props.width - margin.left - margin.right,
            height = this.props.height - margin.top - margin.bottom - 80;
        const barSpacePercentage = 0.35;
        const barWidth = (width / data.length) / (1 + barSpacePercentage);
        const defBarSpace = (width / data.length) * barSpacePercentage;
        // create svg for histogram.
        const gWidth = this.props.width;
        const svg = d3.select(div).append("svg")
            .attr("width", gWidth)
            .attr("height", this.props.height)
            .append("g")
            .attr("transform", `translate(${margin.left}, 40)`);
        svg.append("text")
            .text(this.props.yUnitOfMeasure)
            .attr("transform", `translate(-40, -30)`)
            .classed("chart__unit-of-measure", true);
        // create function for x-axis mapping.
        const x = d3.scaleBand()
            .rangeRound([0, width])
            .domain(data.map(function (d: BarChartGroup) {
                    return d.name;
                }),
            );
        // Create function for y-axis map.
        const y = d3.scaleLinear()
            .range([height, 0])
            .domain(
                [
                    0,
                    d3.max(data, function (d: BarChartGroup) {
                        return d3.max(d.elements, function (d1: BarChartGroupElement) {
                            return d1.value;
                        });
                    }),
                ],
            );
        // Add x-axis to the histogram svg.
        svg.append("g")
            .attr("class", "x axis")
            .attr("transform", `translate(0,${height})`)
            .call((d3 as any).axisBottom().scale(x))
            .selectAll("text")
            .attr("data-id", function (d, i) {
                return "dDateText" + i;
            })
            .attr("class", "dateFieldsClass")
            .style("font-size", "10px")
            .style("fill", "black")
            //  .attr("y", 9)
            //  .attr("x", 3)
            //  .attr("dy", ".35em")
            //  .attr("transform", "rotate(35)")
            .style("text-anchor", "middle");
        const yAxisLeft = (d3 as any).axisRight()
            .scale(y)
            .ticks([5])
            .tickSize(width);
        const customAxis = function (g) {
            g.selectAll("text")
                .attr("x", -30)
                .attr("dy", 4)
                .style("fill", "black");
        };
        svg.append("g")
            .attr("class", "y axis")
            .style("fill", "grey")
            .call(yAxisLeft)
            .call(customAxis);
        svg.selectAll("g.y.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.x.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.y.axis").selectAll("line").style("display", "").style("stroke", "#c0c0c0");
        svg.selectAll("g.x.axis").selectAll("line").style("display", "").style("stroke", "black");
        y.domain([0, d3.max(data, function (d: BarChartGroup) {
            return d3.max(d.elements, function (d1: BarChartGroupElement) {
                return d1.value;
            });
        })]);
        // Create bars for histogram to contain rectangles and freq labels.
        const bars = svg.append("g").attr("class", "bar");
        // create the rectangles.
        const defSpace = 3;
        data.forEach((group: BarChartGroup) => {
            let i = 0;
            group.elements.forEach((element: BarChartGroupElement) => {
                    const intBarWidth = (barWidth / group.elements.length) - defSpace;
                    const space = (i != 0) ? defSpace : 0;
                    const shift = defBarSpace / 2 - defSpace;
                    const barX = shift + x(group.name) + (intBarWidth + space) * i;
                    bars.append("rect")
                        .attr("x", barX)
                        .attr("y", y(element.value))
                        .attr("width", intBarWidth)
                        .attr("data-id", this.randomElementsPrefix + "histogram-bar-" + MarketingTabMapper.codifyEntityName(
                            element.name))
                        .attr("height", height - y(element.value))
                        .attr("fill", (element.color && element.color != "") ? element.color : color)
                        .style("opacity", ".8")
                        .classed(this.randomElementsPrefix + "bars", true)
                        .on("mouseover", (d) => {
                            this.setSelectedItem(element.name);
                        })
                        .on("mouseout", () => {
                            this.setSelectedItem(null);
                        });

                    // Create the frequency labels above the rectangles.
                    bars.append("text")
                        .text(PrettyPrinter.salesQuantity(element.value))
                        .attr(
                            "data-id",
                            this.randomElementsPrefix + "element-frequency-" + MarketingTabMapper.codifyEntityName(
                                element.name),
                        )
                        .attr("x", barX + intBarWidth / 2)
                        .attr("y", y(element.value) - 5)
                        .attr("text-anchor", "middle")
                        .classed("bars-text-field", true)
                        .classed(this.randomElementsPrefix + "bar-frequencies", true)
                        .style("font-size", "12px")
                        .style("display", "none");
                    i++;
                },
            );
        });

        if (data.length > 0 && data[0].elements.length > 0) {
            let dataL = 0;
            const offset = width / data[0].elements.length;
            const legend = svg.selectAll(".legends4")
                .data(data[0].elements)
                .enter()
                .append("g")
                .attr("data-id", (d) => {
                        return this.randomElementsPrefix + "legend-element-" + MarketingTabMapper.codifyEntityName(d.name);
                    },
                )
                .classed(this.randomElementsPrefix + "legend-elements", true)
                .classed("legends4", true)
                .attr("transform", function (d, i) {
                    if (i === 0) {
                        dataL = data[0].elements.length + offset;
                        return "translate(0," + (height + 40) + ")";
                    } else {
                        const newdataL = dataL;
                        dataL += data[0].elements.length + offset;
                        return "translate(" + (newdataL) + "," + (height + 40) + ")";
                    }
                }).on("mouseover", (event, d: BarChartGroupElement) => {
                    this.setSelectedItem(d.name);
                })
                .on("mouseout", () => {
                    this.setSelectedItem(null);
                })
                .style("cursor", "pointer");

            legend.append("rect")
                .attr("x", 0)
                .attr("y", 0)
                .attr("width", 10)
                .attr("height", 10)
                .style("fill", function (d) {
                    return d.color;
                });

            legend.append("text")
                .attr("x", 12)
                .attr("y", 10)
                // .attr("dy", ".35em")
                .text(function (d) {
                    return d.name;
                })
                .attr("class", "textselected")
                .style("text-anchor", "start");
            // .style("font-size", 9)
        }

        if (this.state.selectedItem != null) {
            d3.selectAll(
                "[data-id=" + this.randomElementsPrefix + "element-frequency-" + MarketingTabMapper.codifyEntityName(
                    this.state.selectedItem) + "]")
                .style("display", "");
            d3.selectAll("." + this.randomElementsPrefix + "bars")
                .style("opacity", ".2");
            d3.selectAll(
                "[data-id=" + this.randomElementsPrefix + "histogram-bar-" + MarketingTabMapper.codifyEntityName(
                    this.state.selectedItem) + "]")
                .style("opacity", ".8");
            d3.selectAll("." + this.randomElementsPrefix + "legend-elements")
                .style("opacity", ".2");
            d3.selectAll(
                "[data-id=" + this.randomElementsPrefix + "legend-element-" + MarketingTabMapper.codifyEntityName(
                    this.state.selectedItem) + "]")
                .style("opacity", "1");
        } else {
            d3.selectAll("." + this.randomElementsPrefix + "bar-frequencies")
                .style("display", "none");
            d3.selectAll("." + this.randomElementsPrefix + "bars")
                .style("opacity", ".8");
            d3.selectAll("." + this.randomElementsPrefix + "legend-elements")
                .style("opacity", "1");
        }

        const normalizeText = (text: string): string => {
            if (text.length > 19) {
                text = text.slice(0, 18) + "...";
            }
            return text;
        };
        svg.selectAll("g.x.axis").selectAll("text").each(function (d, i) {
            d3.select(this).text(normalizeText(d3.select(this).text()));
        });
        return div.toReact();
    }
}
